import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X, Phone } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';

const Header = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/quem-somos', label: 'Quem Somos' },
    { path: '/servicos', label: 'Serviços' },
    { path: '/diferenciais', label: 'Diferenciais' },
    { path: '/blog', label: 'Blog' },
    { path: '/contato', label: 'Contato' },
  ];
  const clientAreaLinks = (companyInfo?.clientAreaLinks || []).filter(
    (item) => item && String(item.label || '').trim() !== ''
  );

  const isActive = (path) => location.pathname === path;

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  const logoSrc = isScrolled
    ? `${process.env.PUBLIC_URL}/logo_preta.png`
    : `${process.env.PUBLIC_URL}/logo_branca.png`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-12 w-44 sm:w-52 rounded-lg flex items-center justify-center shadow-md shadow-black/10 bg-white/0 group-hover:shadow-orange-200 transition-shadow duration-300 overflow-hidden">
              <img
                src={logoSrc}
                alt={companyInfo?.name || "Logo"}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden">
              <span className={`font-bold text-xl tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-slate-800' : 'text-white'
              }`}>
                DRSUPORTI
              </span>
              <span className={`text-[10px] tracking-wider uppercase transition-colors duration-300 ${
                isScrolled ? 'text-slate-500' : 'text-white/70'
              }`}>
                Soluções em TI
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'bg-orange-500 text-white'
                    : isScrolled
                    ? 'text-slate-600 hover:text-orange-500 hover:bg-orange-50'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {clientAreaLinks.length > 0 ? (
              <div className="relative group">
                <button
                  type="button"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isScrolled
                      ? 'text-slate-600 hover:text-orange-500 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  Área do cliente
                </button>
                <div
                  className="absolute left-0 top-full pt-2 opacity-0 translate-y-1 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto"
                >
                  <div className="min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl p-2">
                    {clientAreaLinks.map((item) => (
                      <a
                        key={item.label}
                        href={item.href || '#'}
                        className="block px-3 py-2 rounded-lg text-sm text-slate-600 hover:text-orange-500 hover:bg-orange-50"
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={handleWhatsApp}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
            >
              <Phone className="w-4 h-4 mr-2" />
              Fale com um especialista
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors duration-200 ${
              isScrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col p-4 gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive(link.path)
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-600 hover:text-orange-500 hover:bg-orange-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          {clientAreaLinks.length > 0 ? (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Área do cliente
              </div>
              {clientAreaLinks.map((item) => (
                <a
                  key={item.label}
                  href={item.href || '#'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:text-orange-500 hover:bg-orange-50"
                >
                  {item.label}
                </a>
              ))}
            </div>
          ) : null}
          <Button
            onClick={() => {
              handleWhatsApp();
              setIsMobileMenuOpen(false);
            }}
            className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
          >
            <Phone className="w-4 h-4 mr-2" />
            Fale com um especialista
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
